import { Component, HostBinding, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import * as _ from 'underscore';
import { isUndefined, isNullOrUndefined } from '../../../../models/utils';
import { DrNamedQuery } from '../../../../models/dr-named-query';
import { Endpoint, HelpIndex } from '../../../../models/enums';
import { ActionType, ObjectActionListType, Permissions } from '../../../../models/permission';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { KeyValueListComponent } from '../../../key-value-list/key-value-list.component';

@Component({
    standalone: false,
    selector: 'res-email-delivery',
    templateUrl: './email-delivery.component.html',
    styleUrls: ['./email-delivery.component.scss']
})
export class EmailDeliveryComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('emailDeliveryForm', { static: true }) emailDeliveryForm: NgForm;
    @ViewChild(KeyValueListComponent, { static: true }) keyValueList: KeyValueListComponent;

    isBusy = false;
    isBusyMessage: string;
    validChanges = true;
    emailDeliveryEnabled: boolean;
    originalEmailDeliveryEnabled: boolean;
    emailRecipients = [];
    casePermissions: boolean;
    prjHandle: string;
    hasPermissions: boolean;

    get isDirty(): boolean {
        if (this.emailDeliveryForm) {
            return this.emailDeliveryForm.dirty || this.keyValueList.keyValueForm.form.dirty;
        }
        return false;
    }
    set isDirty(value: boolean) {
        if (this.emailDeliveryForm && this.emailDeliveryForm.control) {
            if (value) {
                this.emailDeliveryForm.control.markAsDirty();
            } else {
                this.emailDeliveryForm.control.markAsPristine();
            }
        }
    }
    constructor(private commonService: CommonService, private restService: RestService) { }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string): void {
        this.prjHandle = projectHandle;
        this.hasPermissions = Permissions.checkUserPermission(ObjectActionListType.EMAIL_DELIVERY, ActionType.CREATE);
        this.loadData();
    }

    saveChanges(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if (this.prjHandle) {
                this.isBusyMessage = 'Saving ...';
                this.isBusy = true;
                // remove empty rows
                const recipients = DrNamedQuery.removeEmptyNames(this.emailRecipients).map(r => r.name);

                this.restService.post(Endpoint.projectManager_updateEmailDeliveryPreference, {
                    ownerId: this.prjHandle,
                    reportDeliveryEnabled: this.emailDeliveryEnabled,
                    recipients: recipients
                }).then(() => {
                    this.isDirty = false;
                    this.isBusy = false;
                    this.resetState();
                    this.loadData();
                    resolve(true);
                }, () => {
                    this.isBusy = false;
                    resolve(false);
                });
                this.originalEmailDeliveryEnabled = this.emailDeliveryEnabled;
            } else {
                resolve(false);
            }
        });
    }

    discardChanges(): void {
        this.isDirty = false;
        this.resetState();
        this.loadData();
    }

    loadData(): void {
        if (this.prjHandle) {
            this.isBusy = true;
            this.isBusyMessage = 'Loading...';
            this.emailRecipients = [];
            this.restService.post(Endpoint.projectManager_getEmailDeliveryPreference, { ownerId: this.prjHandle }).then(response => {
                if (response && response.preferences) {
                    this.emailDeliveryEnabled = this.originalEmailDeliveryEnabled = response.preferences.reportDeliveryEnabled;

                    if (response.preferences.recipients) {
                        if (!isUndefined(response.preferences.recipients) && !_.isEmpty(response.preferences.recipients)) {
                            response.preferences.recipients.forEach(element => {
                                const text = new DrNamedQuery({ name: element.trim() });
                                this.emailRecipients.push(text);
                            });
                        }
                        this.emailRecipients.push(new DrNamedQuery({ name: '' }));
                    }
                }
                this.emailRecipients = [].concat(this.emailRecipients);
                this.casePermissions = Permissions.checkUserPermission(ObjectActionListType.PROJECT, ActionType.CREATE);
                this.isBusy = false;

            }, error => {
                this.isBusy = false;
            });
        }
    }

    isDisabled(): boolean {
        let isEnabled = false;
        if (!this.isBusy) {
            if (this.keyValueList.keyValueForm.form.dirty || this.isDirty) {
                isEnabled = true;
            }
        }
        return !isEnabled;
    }

    toggleCheckbox() {
        this.isDirty = this.emailDeliveryEnabled !== this.originalEmailDeliveryEnabled;
    }

    resetState(): void {
        if (!isNullOrUndefined(this.keyValueList)) {
            this.keyValueList.resetState();
        }
        this.emailDeliveryForm.control.markAsPristine();
    }

    openHelp = () => this.commonService.openHelp(HelpIndex.case_email_report_delivery_dlg);
}
